import React, { useState } from 'react';
import { Collapse, Button, Tooltip, Drawer, message, Spin } from 'antd';
import { CopyOutlined, LinkOutlined, ExportOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import * as C from './constants';
import {
  RowContainer,
  RowHeading,
  RowValue,
  SummaryRowContainer,
  SummaryRowValue,
  SummaryRowHeading,
  DQStatusBubble,
  HoverCopyIcon,
  HoverLinkIcon,
  CopiedToClipboardBubble,
  getTruncatedValue,
  TwoColumnSummaryRow,
  getBooleanBubble,
  getFormattedValue,
  formatDQStatus,
  DrawerTitleRow,
} from './styles';
import JSONViewerModal from './JSONViewerModal';

const { Panel } = Collapse;

const getVal = (obj, key) => {
  if (!obj || obj[key] === undefined || obj[key] === null || obj[key] === '') return 'N/A';
  const val = obj[key];
  if (typeof val === 'object' && val !== null) {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return val;
};

const InvariantQualificationDetailPanel = ({ isOpen, onClose, data, adId, handleShare, loading }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [jsonModalValue, setJsonModalValue] = useState(null);
  const [jsonModalTitle, setJsonModalTitle] = useState('JSON Viewer');
  const location = useLocation();
  if (!data) return null;

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1200);
  };

  const renderSummaryRow = (key, value, copyable = false, link = false, isBoolean = false, fullValue = null) => {
    let displayValue = value === undefined || value === null || value === '' ? 'N/A' : value;
    if (isBoolean) {
      displayValue = getBooleanBubble(value);
    } else if (key === 'Restriction Rules') {
      displayValue = getTruncatedValue(value, 2);
    } else if (key === 'DQ Status') {
      const formatted = formatDQStatus(value);
      displayValue = <DQStatusBubble $isQualified={formatted === 'qualified'}>{formatted}</DQStatusBubble>;
    } else if (key === 'Request Body' || key === 'Response Body') {
      displayValue = getTruncatedValue(value);
    } else {
      displayValue = getFormattedValue(key, value);
    }
    // Use fullValue for copy if provided, else value
    const valueToCopy = fullValue !== null && fullValue !== undefined ? fullValue : value;
    return (
      <SummaryRowContainer key={key}>
        <SummaryRowHeading>{key}:</SummaryRowHeading>
        <SummaryRowValue>
          <span style={{ flex: 1, marginRight: 4 }}>{displayValue}</span>
          {copyable && valueToCopy && valueToCopy !== 'N/A' && (
            <HoverCopyIcon className="hover-copy-icon" style={{ position: 'relative' }}>
              <Tooltip title="Copy">
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={() => handleCopy(key, valueToCopy)}
                  tabIndex={-1}
                />
                {copiedKey === key && <CopiedToClipboardBubble>Copied to Clipboard</CopiedToClipboardBubble>}
              </Tooltip>
            </HoverCopyIcon>
          )}
          {link && value && value !== 'N/A' && (
            <HoverLinkIcon className="hover-link-icon">
              <Tooltip title="Open in new tab">
                <Button
                  icon={<LinkOutlined />}
                  size="small"
                  onClick={() => window.open(value, '_blank')}
                  tabIndex={-1}
                />
              </Tooltip>
            </HoverLinkIcon>
          )}
        </SummaryRowValue>
      </SummaryRowContainer>
    );
  };

  const renderRow = (key, value, copyable = false, link = false, fullValue = null) => {
    const isRequestOrResponse = key === 'Request Body' || key === 'Response Body';
    let displayValue = value;
    if (isRequestOrResponse) {
      displayValue = getTruncatedValue(value);
    } else if (value === undefined || value === null || value === '') {
      displayValue = 'N/A';
    }
    const valueToCopy = isRequestOrResponse && fullValue ? fullValue : value;
    const OpenInNewTabIcon = isRequestOrResponse ? ExportOutlined : LinkOutlined;
    const handleOpenInNewTab = () => {
      if (isRequestOrResponse && fullValue && fullValue !== 'N/A') {
        setJsonModalValue(fullValue);
        setJsonModalTitle(key);
        setJsonModalVisible(true);
      } else if (link && value && value !== 'N/A') {
        window.open(value, '_blank');
      }
    };
    return (
      <RowContainer key={key}>
        <RowHeading>{key}:</RowHeading>
        <RowValue>
          <span style={{ flex: 1, marginRight: 4 }}>{displayValue}</span>
          {copyable && valueToCopy && valueToCopy !== 'N/A' && (
            <HoverCopyIcon className="hover-copy-icon" style={{ position: 'relative' }}>
              <Tooltip title="Copy">
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={() => handleCopy(key, valueToCopy)}
                  tabIndex={-1}
                />
                {copiedKey === key && <CopiedToClipboardBubble>Copied to Clipboard</CopiedToClipboardBubble>}
              </Tooltip>
            </HoverCopyIcon>
          )}
          {link && value && value !== 'N/A' && (
            <HoverLinkIcon className="hover-link-icon">
              <Tooltip title="Open in new tab">
                <Button icon={<OpenInNewTabIcon />} size="small" onClick={handleOpenInNewTab} tabIndex={-1} />
              </Tooltip>
            </HoverLinkIcon>
          )}
        </RowValue>
      </RowContainer>
    );
  };

  // --- Section Data ---
  const requestRows = [
    renderRow(
      'Request Body',
      getTruncatedValue(getVal(data, C.INVARIANT_QUALIFICATION_REQUEST_BODY)),
      true,
      true,
      getVal(data, C.INVARIANT_QUALIFICATION_REQUEST_BODY)
    ),
  ];

  const responseRows = [
    renderRow(
      'Response Body',
      getTruncatedValue(getVal(data, C.INVARIANT_QUALIFICATION_RESPONSE_BODY)),
      true,
      true,
      getVal(data, C.INVARIANT_QUALIFICATION_RESPONSE_BODY)
    ),
  ];

  const metadataRows = [
    renderRow('App', getVal(data, C.INVARIANT_QUALIFICATION_APP)),
    renderRow('Session Type', getVal(data, C.INVARIANT_QUALIFICATION_SESSION_TYPE)),
    renderRow('Content Type', getVal(data, C.INVARIANT_QUALIFICATION_CONTENT_TYPE)),
    renderRow('Content Rating', getVal(data, C.INVARIANT_QUALIFICATION_CONTENT_RATING)),
  ];

  const summaryRows = [
    renderSummaryRow('Endpoint', getVal(data, C.INVARIANT_QUALIFICATION_ENDPOINT), true),
    renderSummaryRow('Ad Id', adId || getVal(data, C.INVARIANT_QUALIFICATION_AD_ID), true),
    renderSummaryRow('Proxy URL', getVal(data, C.INVARIANT_QUALIFICATION_PROXY_URL), true),
    <TwoColumnSummaryRow
      key="row1"
      leftLabel="Qualified Ads"
      leftValue={getVal(data, C.INVARIANT_QUALIFICATION_QUALIFIED_ADS)}
      rightLabel="DQ Status"
      rightValue={getVal(data, C.INVARIANT_QUALIFICATION_DQ_STATUS)}
      rightIsDQStatus={true}
      getBooleanBubble={getBooleanBubble}
      getFormattedValue={getFormattedValue}
    />,
    renderSummaryRow('DQ Filter', getVal(data, C.INVARIANT_QUALIFICATION_DQ_FILTER)),
    renderSummaryRow('DQ Dimension', getVal(data, C.INVARIANT_QUALIFICATION_DQ_DIMENSION)),
    renderSummaryRow(
      'Restriction Rules',
      getTruncatedValue(getVal(data, C.INVARIANT_QUALIFICATION_RESTRICTION_RULES), 2),
      true,
      false,
      false,
      getVal(data, C.INVARIANT_QUALIFICATION_RESTRICTION_RULES)
    ),
  ];

  if (loading && !data) {
    return <Spin tip="Loading..." style={{ width: '100%', marginTop: 40 }} />;
  }

  return (
    <Drawer
      title={
        <DrawerTitleRow>
          <span>Detailed Report</span>
          <Tooltip title="Copy shareable link">
            <ShareAltOutlined style={{ cursor: 'pointer', fontSize: 18 }} onClick={handleShare} />
          </Tooltip>
        </DrawerTitleRow>
      }
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={600}
      destroyOnClose
    >
      <Collapse defaultActiveKey={['summary', 'request', 'response', 'metadata']}>
        <Panel header="Summary" key="summary">
          {summaryRows}
        </Panel>
        <Panel header="Request" key="request">
          {requestRows}
        </Panel>
        <Panel header="Response" key="response">
          {responseRows}
        </Panel>
        <Panel header="Request Metadata" key="metadata">
          {metadataRows}
        </Panel>
      </Collapse>
      <JSONViewerModal
        visible={jsonModalVisible}
        onClose={() => setJsonModalVisible(false)}
        json={jsonModalValue}
        title={jsonModalTitle}
      />
    </Drawer>
  );
};

export default InvariantQualificationDetailPanel;
